// TypeScript mirrors of the backend DTOs. Kept manually in sync; the API surface is small.

export type AccountStatus = 'ACTIVE' | 'INACTIVE' | 'TERMINATED';

export type EmployeeDto = {
  id: number;
  userAccountId: number | null;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  middleName: string | null;
  employeeNumber: number;
  workEmail: string;
  personalEmail: string | null;
  workPhone: string | null;
  mobilePhone: string | null;
  dateOfBirth: string | null;
  hireDate: string;
  terminationDate: string | null;
  employmentStatusId: number;
  departmentId: number | null;
  locationId: number | null;
  jobTitleId: number | null;
  supervisorId: number | null;
  createdAt: string;
  updatedAt: string;
  // My Info profile fields. Configurable dropdowns are stored as option ids; labels are resolved
  // client-side via the employee-fields option lists (see useFieldOptions / org lookups).
  accountStatus: AccountStatus;
  inactiveUntil: string | null;
  genderOptionId: number | null;
  maritalStatusOptionId: number | null;
  clothingSizeOptionId: number | null;
  seniorityLevelOptionId: number | null;
  backgroundCheckSupplierOptionId: number | null;
  annualTrainingSupplierOptionId: number | null;
  countryOptionId: number | null;
  employmentTypeOptionId: number | null;
  backgroundCheckDate: string | null;
  annualTrainingDate: string | null;
  addressStreet1: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  addressPostalCode: string | null;
  workSchedule: string | null;
  profilePictureUrl?: string | null;
  // Termination classification (all null on an active employee). terminationReason is also null on
  // a legacy row terminated before it existed - the profile flags that gap for admins. The note and
  // the regretted flag are admin-only; the server blanks them for every other viewer.
  terminationReason: TerminationReason | null;
  terminationNote: string | null;
  regretted: boolean | null;
};

export type EmployeeSummaryDto = {
  id: number;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  workEmail: string;
  workPhone: string | null;
  departmentId: number | null;
  locationId: number | null;
  jobTitleId: number | null;
  supervisorId: number | null;
  userAccountId: number | null;
  accountStatus: AccountStatus | null;
  profilePictureUrl?: string | null;
};

// One row of the global search bar (GET /v1/employees/search-all). Unlike the directory projection
// this includes former employees, so it carries the two employment facts that mark such a row.
export type EmployeeSearchResultDto = {
  id: number;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  workEmail: string;
  departmentId: number | null;
  jobTitleId: number | null;
  accountStatus: AccountStatus | null;
  terminationDate: string | null;
  profilePictureUrl?: string | null;
};

export type AddEmployeeCommand = {
  firstName: string;
  lastName: string;
  preferredName?: string;
  middleName?: string;
  workEmail: string;
  personalEmail?: string;
  workPhone?: string;
  mobilePhone?: string;
  dateOfBirth?: string;
  hireDate: string;
  employmentStatusId: number;
  departmentId?: number;
  locationId?: number;
  jobTitleId?: number;
  supervisorId?: number;
  selectedTaskListIds?: number[];
  genderOptionId?: number;
  seniorityLevelOptionId?: number;
  countryOptionId?: number;
  addressStreet1?: string;
  addressStreet2?: string;
  addressCity?: string;
  topOfOrg?: boolean;
};

// Response of PATCH /v1/employees/{id}/personal: the updated employee plus the server-computed
// split of what happened to each submitted field (keys match /field-behaviors identifiers).
export type UpdatePersonalResponse = {
  employee: EmployeeDto;
  saved: string[];
  sentForApproval: string[];
  skipped: string[];
};

export type DepartmentDto = { id: number; name: string; parentId: number | null; sortOrder: number };
export type LocationDto = {
  id: number;
  name: string;
  countryCode: string;
  timezone: string;
  address: string | null;
  archived: boolean;
};
export type JobTitleDto = { id: number; name: string; payGrade: string | null; sortOrder: number };
export type EmploymentStatusDto = {
  id: number;
  code: string;
  name: string;
  loginAllowed: boolean;
  countsInHeadcount: boolean;
  sortOrder: number;
};
export type HolidayDto = {
  id: number;
  name: string;
  date: string;
  countryCodes: string[];
  locationIds?: number[];
  recurring?: boolean;
  recurringEndYear?: number | null;
  archived?: boolean;
  version?: number;
  updatedAt?: string | null;
  updatedByName?: string | null;
};

export type RoleDto = {
  id: number;
  code: string;
  name: string;
  description: string;
};

export type SettingsCatalog = {
  id: string;
  name: string;
  description: string;
  status: 'READ_ONLY' | 'COMING_SOON';
  endpoint: string;
};

export function displayName(p: {
  firstName: string;
  lastName: string;
  preferredName: string | null;
}): string {
  if (p.preferredName && p.preferredName.length > 0) {
    return `${p.preferredName} ${p.lastName}`;
  }
  return `${p.firstName} ${p.lastName}`;
}

export type TimeOffCategoryDto = {
  id: number;
  code: string;
  name: string;
  color: string;
  icon: string;
  paid: boolean;
  calendarVisible: boolean;
  sortOrder: number;
};

export type TimeOffBalanceDto = {
  employeeId: number;
  categoryId: number;
  asOf: string;
  accrued: string;
  pending: string;
  available: string;
};

export type TimeOffRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'PENDING_CHANGE'
  | 'DENIED'
  | 'CANCELLED'
  | 'PARTIALLY_CANCELLED';

export type TimeOffRequestDto = {
  id: number;
  employeeId: number;
  categoryId: number | null;
  startDate: string;
  endDate: string;
  days: string;
  note: string | null;
  status: TimeOffRequestStatus;
  approverId: number | null;
  decidedAt: string | null;
  decidedByUserId: number | null;
  decisionComment: string | null;
  createdAt: string;
  updatedAt: string;
  /** Per-day model: calendar dates within [startDate, endDate] that the employee did NOT take (excluded). null = "all weekdays minus location holidays". */
  excludedDates?: string[] | null;
  /** Sparse fractional overlay keyed by ISO date, holding the amount only for taken days below 1 (BigDecimal arrives as a string). null = every taken day whole. */
  partialDays?: Record<string, string> | null;
  /** Optional PDF/JPG files attached to the request. Populated on the single-request detail read; absent/empty on list reads. */
  attachments?: TimeOffAttachmentDto[] | null;
  /**
   * When status is PENDING_CHANGE these carry the edit or cancellation awaiting re-approval; the
   * top-level fields still describe the original approved leave that stays in effect until decided.
   */
  proposedCategoryId?: number | null;
  proposedStartDate?: string | null;
  proposedEndDate?: string | null;
  proposedDays?: string | null;
  proposedExcludedDates?: string[] | null;
  proposedPartialDays?: Record<string, string> | null;
  proposedNote?: string | null;
  proposedCancellation?: boolean;
  proposedCancelReason?: string | null;
  /**
   * The day after a parked cancellation was made, stored for every parked cancellation: the first day
   * it refunds when it falls inside the leave after its first day (a partial cancellation of leave in
   * progress), the days before it staying used. null unless a cancellation is parked.
   */
  proposedCancelledFrom?: string | null;
  /**
   * First refunded day of a partial cancellation: the days strictly before it stayed used, and
   * startDate/endDate/days still describe the whole leave as originally booked. null for every
   * other status and for leaves partially cancelled before the split was stored.
   */
  cancelledFrom?: string | null;
  /**
   * Days the used portion of a PARTIALLY_CANCELLED leave still consumes, typed like `days` above.
   * Resolved by the calendar feed only, since counting it needs the per-day model; null everywhere
   * else and for every other status, where `days` is the whole count.
   */
  usedDays?: string | null;
  /** Where the request stands in the signing chain of its leave request form. */
  signingStage: SigningStage;
  /**
   * True while its leave request form is in flight, which blocks editing. Only the request reads
   * resolve it (the employee's list, the single request, history); every other read reports false.
   */
  signedFormLocked: boolean;
};

/** One taken date and the fraction of a day it consumes; only fractional entries are sent. */
export type DayAmount = { date: string; amount: number };

/** One optional file attached to a time-off request (PDF/JPG, at most 3 MB). */
export type TimeOffAttachmentDto = {
  id: number;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: string;
};

export type CalendarEntry = {
  request: TimeOffRequestDto;
  /** Subject's display name, resolved server-side so terminated/out-of-scope employees still show a name. */
  employeeName: string;
  excludedDates: string[];
};

/** Birthday or work-anniversary occurrence on the calendar; years is 0 for birthdays. */
export type CalendarCelebration = {
  employeeId: number;
  displayName: string;
  date: string;
  years: number;
};

export type SubmitTimeOffRequestCommand = {
  employeeId: number;
  categoryId: number;
  startDate: string;
  endDate: string;
  note?: string;
  /** Present only when an admin submits on behalf of another employee. */
  targetEmployeeId?: number;
  /** Per-day Amount table: the dates the employee marked as taken (value 1). null = default (all weekdays minus holidays). */
  takenDates?: string[] | null;
  /** Sparse fractional overlay: only taken days below a whole day. Sent only when the policy allows half days. */
  dayAmounts?: DayAmount[] | null;
};

export type TimeOffDecisionCommand = {
  decision: 'APPROVE' | 'DENY';
  comment?: string;
};

/**
 * Where a time-off request stands in the signing chain of its leave request form; the leave-document
 * types below mirror leavedocument.api. NONE is every request outside the chain; EMPLOYEE waits for
 * the requester's signature (a new request stays hidden until then); DIRECTOR waits for the regional
 * director's signature.
 */
export type SigningStage = 'NONE' | 'EMPLOYEE' | 'DIRECTOR';

/** MIGRATION is the one-time backfill form of leave approved before its office signed leave. */
export type LeaveDocumentKind = 'NEW' | 'CHANGE' | 'MIGRATION';

export type LeaveDocumentState =
  | 'AWAITING_EMPLOYEE'
  | 'AWAITING_SUPERVISOR'
  | 'AWAITING_DIRECTOR'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'DISCARDED';

export type LeaveDocumentEndReason =
  | 'EMPLOYEE_DECLINED'
  | 'EXPIRED'
  | 'SUPERVISOR_DENIED'
  | 'DIRECTOR_DECLINED'
  | 'REQUEST_CANCELLED'
  | 'CHANGE_WITHDRAWN'
  | 'EMPLOYEE_TERMINATED';

/**
 * One signing round (version) of a request's leave request form. A change carries its proposed
 * values. supervisorName is whoever approved the request as its supervisor, set once they approved;
 * directorName the director the director's form went to, set once it went out (its signer once
 * directorSignedAt is set) and null while the form is held for a missing director; either is null when
 * the person is unknown. canOpenPdf is true when the round has a PDF the viewer may open through its
 * pdf endpoint.
 */
export type LeaveDocumentDto = {
  id: number;
  requestId: number;
  employeeId: number;
  versionNo: number;
  kind: LeaveDocumentKind;
  state: LeaveDocumentState;
  endReason: LeaveDocumentEndReason | null;
  categoryId: number;
  categoryName: string;
  startDate: string;
  endDate: string;
  /** BigDecimal, serialised as a JSON number. */
  days: number;
  createdAt: string;
  employeeSignedAt: string | null;
  supervisorApprovedAt: string | null;
  supervisorName: string | null;
  directorSignedAt: string | null;
  directorName: string | null;
  endedAt: string | null;
  declineReason: string | null;
  retainedDocumentId: number | null;
  canOpenPdf: boolean;
  /** The one date a migration form prints (yyyy-MM-dd); null for every other kind. */
  formDate: string | null;
};

/** Who signs a form of a round: the requesting employee their own form, the director the director's. */
export type LeaveFormRole = 'EMPLOYEE' | 'DIRECTOR';

/**
 * One form of a round of a request's leave request form and the role its signer plays there, as the
 * sign page opens it: by request (the form waiting for the requester) or by the signing document.
 */
export type LeaveSigningStepDto = {
  leaveDocumentId: number;
  requestId: number;
  documentId: number;
  recipientId: number;
  role: LeaveFormRole;
  state: LeaveDocumentState;
  kind: LeaveDocumentKind;
};

export type TimeOffEditCommand = {
  categoryId: number;
  startDate: string;
  endDate: string;
  note?: string;
  /** Per-day Amount table: the dates marked as taken. null = default whole-range model. */
  takenDates?: string[] | null;
  /** Sparse fractional overlay: only taken days below a whole day. Sent only when the policy allows half days. */
  dayAmounts?: DayAmount[] | null;
};

/**
 * What one revision in the history of a time-off request records. Values are add-only on the server,
 * so the timeline words one it does not know as "Updated by".
 */
export type RevisionAction =
  | 'SUBMITTED'
  | 'EDITED'
  | 'CHANGE_REQUESTED'
  | 'CANCELLATION_REQUESTED'
  | 'SUPERVISOR_APPROVED'
  | 'APPROVED'
  | 'DENIED'
  | 'CHANGE_APPROVED'
  | 'CHANGE_DENIED'
  | 'CANCELLATION_APPROVED'
  | 'CANCELLATION_DENIED'
  | 'CHANGE_WITHDRAWN'
  | 'CANCELLED'
  | 'IMPORTED'
  | 'BASELINE';

/**
 * A run of taken days in a request's history: whole days from start to end that only a working day
 * not taken would break, or one day under a whole day on its own. days (a BigDecimal, serialised as a
 * JSON number) is the sum of the run's amounts.
 */
export type DayRun = { start: string; end: string; days: number };

/**
 * One side of a revision: the category as it is called now (null once the category is deleted), the
 * total days, the taken days as runs in date order, and the note. A side with no days left (a
 * cancellation's after) has days 0 and no runs.
 */
export type RevisionDays = {
  categoryId: number | null;
  categoryName: string | null;
  days: number;
  runs: DayRun[];
  note: string | null;
};

/**
 * One entry in the history of a time-off request, oldest first. actorName is null when no person
 * acted (a scheduled job, the import, the baseline). before is null for the actions that only record
 * a state, and removed and added, the per-date differences as runs, are null exactly when before is.
 * reason is a cancellation's reason, on the cancellation actions only.
 */
export type RequestRevisionDto = {
  revisionNo: number;
  action: RevisionAction;
  actorName: string | null;
  at: string;
  status: TimeOffRequestStatus;
  before: RevisionDays | null;
  after: RevisionDays;
  removed: DayRun[] | null;
  added: DayRun[] | null;
  reason: string | null;
};

// ---- Settings §1 Access Levels -----------------------------------------------

export type PopulationScopeType =
  | 'ALL_EMPLOYEES'
  | 'DIRECT_REPORTS'
  | 'SELF_ONLY'
  | 'SPECIFIC_LIST';

export type PermissionLevel = 'NO_ACCESS' | 'VIEW_ONLY' | 'EDIT';

export type ModuleToggles = {
  settings_access: boolean;
  files_page_access: boolean;
  approve_actions: boolean;
};

// One flat tab-code -> permission map, as stored per scope band.
export type PermissionMatrix = Record<string, PermissionLevel>;

// The three scope bands, keyed by band name to a flat matrix. A band answers "what does this level
// see on ANOTHER person's profile given the relationship": SELF = own profile, DIRECT_REPORTS =
// their direct reports, EVERYONE = anyone else in scope. Distinct from PopulationScopeType, which
// chooses WHO is visible at all.
export type BandedPermissionMatrix = Record<PermissionBand, PermissionMatrix>;

export type AccessLevelDto = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  isSeededDefault: boolean;
  populationScopeType: PopulationScopeType;
  scopeValue: Record<string, unknown> | null;
  moduleToggles: ModuleToggles;
  // Flat SELF band, kept for pre-band callers.
  permissionMatrix: PermissionMatrix;
  // All three bands hydrated (SELF/DIRECT_REPORTS/EVERYONE). Optional so a stale cached payload from
  // before the banded rollout still typechecks; the drawer falls back to flat->SELF when absent.
  bandedPermissionMatrix?: BandedPermissionMatrix;
  // Members the Employees tab also lists - the figure every surface shows.
  employeeCount: number;
  // Members that figure leaves out: accounts kept for terminated employees. They are hidden from the
  // tab but still hold the level, so they are what blocks deleting it. Optional so a cached payload
  // from before the field existed still typechecks.
  retainedMemberCount?: number;
  sortOrder: number;
  version: number;
  updatedAt: string | null;
  updatedByName: string | null;
};

export type UserLoginRecordDto = {
  userId: number;
  workEmail: string;
  displayName: string;
  accessLevelId: number;
  accessLevelCode: string;
  accessLevelName: string;
  accountStatus: string;
  lastLoginAt: string | null;
};

// The server-resolved permission set of the EFFECTIVE role - the same object the backend gates
// enforce with, serialized verbatim. The SPA derives all page/tab/module gating from this.
export type PermissionSetDto = {
  admin: boolean;
  tabs: Record<string, PermissionLevel>;
  modules: Record<string, boolean>;
};

export type MeResponse = {
  realUserId: number;
  realDisplayName: string;
  realRoleCode: string;
  effectiveUserId: number;
  effectiveDisplayName: string;
  effectiveRoleCode: string;
  impersonating: boolean;
  impersonationExpiresAt: string | null;
  permissions: PermissionSetDto;
};

// GET /v1/employees/{id}/viewer-grants - the CALLER's server-resolved grants on ONE target
// employee. `band` is the matched scope band (SELF / DIRECT_REPORTS / EVERYONE), null for an
// admin viewer (admin short-circuits every check and leaves `tabs` empty).
export type PermissionBand = 'SELF' | 'DIRECT_REPORTS' | 'EVERYONE';

export type ViewerGrants = {
  admin: boolean;
  band: PermissionBand | null;
  tabs: Record<string, PermissionLevel>;
};

// Org-wide Timesheet feature toggle.
export type TimesheetVisibility = 'OFF' | 'ADMIN_ONLY' | 'ALL';

// GET /v1/timesheet/visibility - effective visibility for the current (effective) user.
// `canView` already folds in the state and the caller's role, so the UI just reads the boolean.
export type TimesheetVisibilityStatus = {
  visibility: TimesheetVisibility;
  canView: boolean;
};

// GET/PUT /v1/settings/timesheet/visibility - admin view of the setting with the lock version.
export type TimesheetVisibilityView = {
  visibility: TimesheetVisibility;
  version: number;
  updatedAt: string | null;
  updatedByName: string | null;
};

export type UserAccountDto = {
  id: number;
  workEmail: string;
  displayName: string;
  role: RoleDto;
  status: string;
  lastLoginAt: string | null;
};

export type AccessLevelCreateRequest = {
  code: string;
  name: string;
  description?: string;
  moduleToggles: ModuleToggles;
  // Flat SELF band, still required by the backend request record.
  permissionMatrix: PermissionMatrix;
  // All three bands; when present it supersedes the flat map. The editor always sends it.
  bandedPermissionMatrix: BandedPermissionMatrix;
};

export type AccessLevelUpdateRequest = {
  name: string;
  description?: string;
  moduleToggles: ModuleToggles;
  // Flat SELF band, still required by the backend request record.
  permissionMatrix: PermissionMatrix;
  // All three bands; when present it supersedes the flat map. The editor always sends it.
  bandedPermissionMatrix: BandedPermissionMatrix;
  version?: number;
};

export type ChangeRoleRequest = {
  newRoleCode: string;
};

// ---- Settings §1.4 Access Level History --------------------------------------

export type AccessLevelDiffDto = {
  before: string | null;
  after: string | null;
};

export type AccessLevelAuditEventDto = {
  id: number;
  timestamp: string;
  actorDisplayName: string;
  actorWorkEmail: string | null;
  actorId: number | null;
  action: string;
  diff: AccessLevelDiffDto | null;
};

export type AccessLevelActorDto = {
  id: number;
  displayName: string;
  workEmail: string;
};

export type AccessLevelHistoryResponse = {
  events: AccessLevelAuditEventDto[];
  actors: AccessLevelActorDto[];
};

// ---- Company Directory §4.3 --------------------------------------------------

export type DirectoryCardFieldDto = {
  fieldKey: string;
  label: string;
  visible: boolean;
  sortOrder: number;
  locked: boolean;
};

export type DirectReportRef = {
  employeeId: number;
  name: string;
};

export type DirectoryEntryDto = {
  employeeId: number;
  name: string;
  jobTitle: string | null;
  department: string | null;
  location: string | null;
  workEmail: string | null;
  mobilePhone: string | null;
  hireDate: string | null;
  employmentStatus: string | null;
  managerId: number | null;
  managerName: string | null;
  directReports: DirectReportRef[];
  // Structural rendering attributes for the People Directory tab (not gated by the §4.3 card-field
  // config). Older clients/cached payloads may lack these, so they are optional.
  lastName?: string | null;
  accountStatus?: string | null;
  onLeave?: boolean;
  avatarUrl?: string | null;
};

export type DirectoryViewDto = {
  fields: DirectoryCardFieldDto[];
  entries: DirectoryEntryDto[];
};

// GET /v1/directory/access - the caller's own visibility flags, for gating navigation.
export type DirectoryAccessDto = {
  canViewDirectory: boolean;
  canViewOrgChart: boolean;
};

// ---- People page List tab (GET /v1/people/list) ------------------------------

export type PeopleListRowDto = {
  employeeId: number;
  name: string;
  accountStatus: string | null;
  hireDate: string | null;
  employmentStatus: string | null;
  employmentStatusCode: string | null;
  location: string | null;
  jobTitle: string | null;
  managerId: number | null;
  managerName: string | null;
  seniorityLabel: string | null;
  senioritySortOrder: number | null;
  terminationDate: string | null;
  activeDirectReportCount: number;
  // Attrition classification, read by the payforge-finance roster sync. The note and the regretted
  // flag are null outside the ADMIN scope.
  terminationReason: TerminationReason | null;
  terminationNote: string | null;
  regretted: boolean | null;
};

export type PeopleListResponse = {
  scope: 'ADMIN' | 'MANAGER';
  rows: PeopleListRowDto[];
};

export type DirectoryVisibilityRowDto = {
  accessLevelId: number;
  code: string;
  name: string;
  orgChartVisible: boolean;
  directoryVisible: boolean;
  locked: boolean;
};

export type DirectoryVisibilityDto = {
  rows: DirectoryVisibilityRowDto[];
  everyoneCanViewDirectory: boolean;
  everyoneCanViewOrgChart: boolean;
  visibilityVersion: number;
  visibilityUpdatedAt: string | null;
  visibilityUpdatedByName: string | null;
};

export type DirectoryCardFieldsView = {
  fields: DirectoryCardFieldDto[];
  cardFieldsVersion: number;
  cardFieldsUpdatedAt: string | null;
  cardFieldsUpdatedByName: string | null;
};

// ---- Settings §11 Time Off ---------------------------------------------------

export type PolicyType = 'TRADITIONAL' | 'FLEXIBLE';
export type AccrualFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'TWICE_MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type TimeOffPolicyDetailDto = {
  id: number;
  categoryId: number;
  name: string;
  policyType: PolicyType;
  frequency: AccrualFrequency | null;
  accrualRateDays: string | null;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  dayOfMonth2: number | null;
  monthInQuarter: number | null;
  accrualMonth: number | null;
  accrualDay: number | null;
  waitingDays: number | null;
  capDays: string | null;
  carryoverEnabled: boolean;
  resetMonth: number;
  resetDay: number;
  enrolledCount: number;
  version: number;
  allowHalfDays: boolean;
};

export type TimeOffCategoryWithStats = {
  id: number;
  code: string;
  name: string;
  color: string | null;
  icon: string;
  paid: boolean;
  calendarVisible: boolean;
  sortOrder: number;
  policyCount: number;
  employeeCount: number;
  version: number;
};

export type TimeOffSettingsOverviewDto = {
  categories: TimeOffCategoryWithStats[];
};

export type TimeOffCategoryRequest = {
  name: string;
  color?: string | null;
  icon: string;
  paid: boolean;
  calendarVisible: boolean;
  sortOrder?: number | null;
  version?: number | null;
};

export type TimeOffCategoryReorderRequest = {
  orderedIds: number[];
};

export type TimeOffPolicyRequest = {
  name: string;
  policyType: PolicyType;
  frequency?: AccrualFrequency | null;
  accrualRateDays?: string | null;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  dayOfMonth2?: number | null;
  monthInQuarter?: number | null;
  accrualMonth?: number | null;
  accrualDay?: number | null;
  waitingDays?: number | null;
  capDays?: string | null;
  carryoverEnabled: boolean;
  resetMonth: number;
  resetDay: number;
  version?: number | null;
  categoryId?: number | null;
  allowHalfDays: boolean;
};

export type PolicyEnrollmentDto = {
  id: number;
  policyId: number;
  employeeId: number;
  employeeDisplayName: string;
  enrolledAt: string;
};

export type EmployeeEnrollmentDto = {
  enrollmentId: number;
  categoryId: number;
  categoryName: string;
  icon: string;
  color: string;
  policyId: number;
  policyName: string;
  policyType: PolicyType;
  enrolledAt: string;
  effectiveDate: string;
  allowHalfDays: boolean;
};

export type EnrolledBalanceDto = {
  employeeId: number;
  categoryId: number;
  categoryName: string;
  color: string;
  icon: string;
  flexible: boolean;
  asOf: string;
  accrued: string | null;
  pending: string | null;
  available: string | null;
  scheduled: string | null;
  usedYtd: string | null;
  allowHalfDays: boolean;
};

/**
 * One location on the Leave documents tab of Settings > Time off. Employees at an enabled location
 * sign a leave request form, which the location's director signs after the supervisor approves.
 * The director warnings describe the saved director: directorTerminated when they have left,
 * directorCannotSignIn when they still work here but cannot sign in, and heldRoundCount counts the
 * forms waiting for a director to be assigned. version is the optimistic-lock token the save sends
 * back (0 for a location never saved).
 */
export type LeaveDocumentLocationDto = {
  locationId: number;
  name: string;
  archived: boolean;
  enabled: boolean;
  directorEmployeeId: number | null;
  directorName: string | null;
  directorTerminated: boolean;
  directorCannotSignIn: boolean;
  heldRoundCount: number;
  version: number;
};

/**
 * How one time-off category is worded on the leave request form: phraseRo and phraseEn fill the
 * request sentence and the approval order, null prints the category name. paid is a hint only, the
 * form appends "plătit" itself for paid categories.
 */
export type CategoryWordingDto = {
  categoryId: number;
  name: string;
  paid: boolean;
  phraseRo: string | null;
  phraseEn: string | null;
  version: number;
};

export type LeaveDocumentSettingsDto = {
  locations: LeaveDocumentLocationDto[];
  categories: CategoryWordingDto[];
};

export type LeaveDocumentLocationRequest = {
  enabled: boolean;
  directorEmployeeId: number | null;
  version: number;
};

/** Trimmed phrases, a blank one sent as null. */
export type CategoryWordingRequest = {
  phraseRo: string | null;
  phraseEn: string | null;
  version: number;
};

/**
 * A location in the scope of the Moldova migration: an enabled location and its director, who must be
 * able to sign (directorReady) before the migration may start.
 */
export type LeaveMigrationLocationDto = {
  locationId: number;
  name: string;
  directorName: string | null;
  directorReady: boolean;
};

/**
 * Where one leave request stands in the Moldova migration: NOT_STARTED has no form yet, QUEUED waits to
 * be issued, FAILED could not be issued, HELD was signed by the employee and waits for a director who
 * can sign; the other states are those of its leave document round.
 */
export type LeaveMigrationItemState =
  | 'NOT_STARTED'
  | 'QUEUED'
  | 'FAILED'
  | 'AWAITING_EMPLOYEE'
  | 'HELD'
  | 'AWAITING_DIRECTOR'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'DISCARDED';

/**
 * One approved leave request of the Moldova migration. requestedOn is the date of the original request
 * and formDate the one date the form prints; error is why issuing its form failed. restartable is true
 * only for a form its employee or director declined, which an admin may restart with a fresh one.
 */
export type LeaveMigrationItemDto = {
  requestId: number;
  leaveDocumentId: number | null;
  employeeId: number;
  employeeName: string;
  requestedOn: string;
  startDate: string;
  endDate: string;
  categoryName: string;
  formDate: string;
  state: LeaveMigrationItemState;
  employeeSigned: boolean;
  employeeSignedAt: string | null;
  directorSigned: boolean;
  directorSignedAt: string | null;
  error: string | null;
  restartable: boolean;
};

/**
 * The Moldova migration: its window on the request date, its locations, the counts of its leave
 * requests by where they stand, and one page of them. ended counts the rejected, cancelled and
 * discarded forms; the skipped counts are requests never enqueued; issuedNow is set only on the answer
 * of an issue call, counting the forms that call issued.
 */
export type LeaveMigrationStatusDto = {
  windowFrom: string;
  windowTo: string;
  locations: LeaveMigrationLocationDto[];
  total: number;
  employees: number;
  notStarted: number;
  queued: number;
  failed: number;
  held: number;
  awaitingEmployee: number;
  awaitingDirector: number;
  completed: number;
  ended: number;
  skippedNoAccount: number;
  skippedUntrimmable: number;
  issuedNow: number;
  items: LeaveMigrationItemDto[];
  hasMore: boolean;
};

/**
 * One new leave request form the next start of the Moldova migration would enqueue: the request, its
 * employee's legal name, the leave the form documents and the one date the form prints.
 */
export type LeaveMigrationPreviewItemDto = {
  requestId: number;
  employeeId: number;
  employeeName: string;
  startDate: string;
  endDate: string;
  categoryName: string;
  formDate: string;
};

/**
 * The new forms the next start of the Moldova migration would enqueue, read without changing anything:
 * how many, for how many colleagues, and every one of them in the order the tab lists them. Rounds
 * already queued, held or failed, which a start continues or retries, are not listed.
 */
export type LeaveMigrationPreviewDto = {
  total: number;
  employees: number;
  items: LeaveMigrationPreviewItemDto[];
};

/**
 * Why the Moldova migration skips an approved leave: its employee cannot sign in (no account, or left),
 * or the leave kept no working day for a form to document.
 */
export type LeaveMigrationSkippedReason = 'CANNOT_SIGN_IN' | 'NO_DAYS_TO_DOCUMENT';

/**
 * One approved leave the Moldova migration skips: the request, its employee's legal name, the leave as
 * requested (untrimmed, since no form documents it), its category and why it is skipped.
 */
export type LeaveMigrationSkippedItemDto = {
  requestId: number;
  employeeId: number;
  employeeName: string;
  startDate: string;
  endDate: string;
  categoryName: string;
  reason: LeaveMigrationSkippedReason;
};

/**
 * The approved leave the Moldova migration skips, read without changing anything: how many, and every
 * one of them in the order the tab lists its leave requests.
 */
export type LeaveMigrationSkippedDto = {
  total: number;
  items: LeaveMigrationSkippedItemDto[];
};

// ---- Settings §10 Performance -----------------------------------------------

export type ReviewCycleScheduleMode = 'FIXED_DATE' | 'BY_HIRE_DATE';
export type ReviewCycleStatus = 'DRAFT' | 'ACTIVE' | 'DEACTIVATED';
export type FeedbackCadence = 'ONCE' | 'WEEKLY' | 'EVERY_TWO_WEEKS' | 'MONTHLY';
export type QuestionKind = 'PEER' | 'SELF' | 'MANAGER';

export type CycleSummaryDto = {
  id: number;
  name: string;
  scheduleMode: ReviewCycleScheduleMode;
  status: ReviewCycleStatus;
  employeeCount: number;
  version: number;
};

export type QuestionDto = {
  id: number | null;
  questionText: string;
  sortOrder: number;
};

export type ReviewCycleDetailDto = {
  id: number;
  name: string;
  scheduleMode: ReviewCycleScheduleMode;
  includeAll: boolean;
  locationIds: number[];
  specificEmployeeIds: number[];
  status: ReviewCycleStatus;
  feedbackFirstReminderDate: string | null;
  feedbackCadence: FeedbackCadence | null;
  feedbackHireOffsetMonths: number | null;
  assessmentsIncluded: boolean;
  assessmentWindowStart: string | null;
  assessmentHireOffsetMonths: number | null;
  allowFileUpload: boolean;
  fileUploadRequired: boolean;
  peerFeedbackIncluded: boolean;
  version: number;
  peerQuestions: QuestionDto[];
  selfQuestions: QuestionDto[];
  managerQuestions: QuestionDto[];
};

export type QuestionEntry = {
  id: number | null;
  questionText: string;
  sortOrder: number;
};

export type ReviewCycleSaveRequest = {
  name: string;
  scheduleMode: ReviewCycleScheduleMode;
  includeAll: boolean;
  locationIds: number[];
  specificEmployeeIds: number[];
  feedbackFirstReminderDate: string | null;
  feedbackCadence: FeedbackCadence | null;
  feedbackHireOffsetMonths: number | null;
  assessmentsIncluded: boolean;
  assessmentWindowStart: string | null;
  assessmentHireOffsetMonths: number | null;
  allowFileUpload: boolean;
  fileUploadRequired: boolean;
  peerFeedbackIncluded: boolean;
  version: number | null;
  peerQuestions: QuestionEntry[];
  selfQuestions: QuestionEntry[];
  managerQuestions: QuestionEntry[];
};

export type MatchedEmployeeDto = {
  id: number;
  fullName: string;
  jobTitle: string | null;
};

// ---- Performance tab (runtime) ------------------------------------------------

export type PerfAnswerRow = {
  questionText: string;
  answerText: string;
};

export type PerfPendingRequestRow = {
  requestId: number;
  peerName: string;
  createdAt: string;
};

export type PerfSubmittedFeedbackRow = {
  peerName: string;
  submittedAt: string;
  answers: PerfAnswerRow[];
};

export type PerfFeedbackBlock = {
  activeCycleId: number | null;
  capRemaining: number;
  pendingRequests: PerfPendingRequestRow[];
  submittedFeedback: PerfSubmittedFeedbackRow[];
};

export type PerfAssessmentSummary = {
  id: number;
  cycleId: number;
  cycleName: string;
  windowStart: string;
  windowEnd: string;
  selfSubmitted: boolean;
  managerSubmitted: boolean;
  skipped: boolean;
  released: boolean;
};

export type PerformanceTabDto = {
  assessments: PerfAssessmentSummary[];
  feedbackBlock: PerfFeedbackBlock | null;
};

export type PerfQuestionDto = {
  id: number;
  questionText: string;
  kind: QuestionKind;
};

export type PerfAnswerDto = {
  questionId: number;
  answerText: string;
};

export type PerfFileDto = {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
};

export type AssessmentFormDto = {
  instanceId: number;
  allowFileUpload: boolean;
  fileUploadRequired: boolean;
  selfQuestions: PerfQuestionDto[];
  managerQuestions: PerfQuestionDto[];
  selfAnswers: PerfAnswerDto[];
  managerAnswers: PerfAnswerDto[];
  selfFiles: PerfFileDto[];
  managerFiles: PerfFileDto[];
};

export type PerfAnswerResultDto = {
  questionText: string;
  answerText: string;
};

export type PeerFeedbackResultDto = {
  peerLabel: string;
  answers: PerfAnswerResultDto[];
};

export type AssessmentResultsDto = {
  instanceId: number;
  selfAnswers: PerfAnswerResultDto[];
  managerAnswers: PerfAnswerResultDto[];
  peerFeedback: PeerFeedbackResultDto[];
};

export type PendingFeedbackRequestDto = {
  requestId: number;
  subjectName: string;
  cycleName: string;
  questions: PerfQuestionDto[];
};

// ---- Employee Onboarding tab --------------------------------------------------

export type OnboardingTaskDto = {
  id: number;
  title: string;
  assigneeRole: string;
  dueDate: string;
  completedOn: string | null;
  required: boolean;
  sortOrder: number;
};

// A snapshot attachment on a sent onboarding task, downloaded via the runtime endpoint.
export type OnboardingInstanceTaskAttachmentDto = {
  id: number;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
};

export type OnboardingTaskInstanceDto = {
  id: number;
  title: string;
  taskListName: string | null;
  taskListOrdering: number;
  description: string | null;
  assigneeRole: string | null;
  assigneeUserId: number | null;
  assigneeLabel: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  completedOn: string | null;
  completedByUserId: number | null;
  completedByName: string | null;
  sortOrder: number;
  attachments: OnboardingInstanceTaskAttachmentDto[];
};

// "Add Task List(s)" drawer (runtime): task lists with assignees resolved against the employee.
export type OnboardingAssignableTaskDto = {
  taskId: number;
  title: string;
  assigneeDisplay: string | null;
  alreadyAssigned: boolean;
};

export type OnboardingAssignableListDto = {
  listId: number;
  name: string;
  icon: string;
  ordering: number;
  tasks: OnboardingAssignableTaskDto[];
};

// Task Detail drawer comment thread.
export type OnboardingTaskCommentDto = {
  id: number;
  taskId: number;
  authorUserId: number | null;
  authorName: string | null;
  body: string;
  createdAt: string;
};

// ---- Settings §9 Onboarding task lists (for AddEmployee picker) ---------------

export type OnboardingTaskListDto = {
  id: number;
  name: string;
  icon: string;
  ordering: number;
  isSystem: boolean;
  taskCount: number;
};

// ---- Settings §8 Offboarding task lists (Offboarding tab task-list picker) -----

export type OffboardingTaskListDto = {
  id: number;
  name: string;
  icon: string;
  ordering: number;
  isSystem: boolean;
  taskCount: number;
};

// ---- Employee Offboarding tab (PFHR-PROFILE-001h) ------------------------------

// A snapshot attachment on a sent offboarding task, downloaded via the runtime endpoint.
export type OffboardingInstanceTaskAttachmentDto = {
  id: number;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
};

export type OffboardingTaskInstanceDto = {
  id: number;
  title: string;
  taskListName: string | null;
  taskListOrdering: number;
  description: string | null;
  assigneeRole: string | null;
  assigneeUserId: number | null;
  assigneeLabel: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  completedOn: string | null;
  completedByUserId: number | null;
  completedByName: string | null;
  sortOrder: number;
  attachments: OffboardingInstanceTaskAttachmentDto[];
};

export type OffboardingTaskCommentDto = {
  id: number;
  taskId: number;
  authorUserId: number | null;
  authorName: string | null;
  body: string;
  createdAt: string;
};

export type OffboardingAssignableTaskDto = {
  taskId: number;
  title: string;
  assigneeDisplay: string | null;
  alreadyAssigned: boolean;
};

export type OffboardingAssignableListDto = {
  listId: number;
  name: string;
  icon: string;
  ordering: number;
  tasks: OffboardingAssignableTaskDto[];
};

// Add Task List(s) drawer payload: stored Expected Termination Date (null until first save) + lists.
export type OffboardingAvailableTaskListsDto = {
  expectedTerminationDate: string | null;
  lists: OffboardingAssignableListDto[];
};

// ---- Home W8 Pending Approvals card ------------------------------------------

export type PendingApprovalEntry = {
  request: TimeOffRequestDto;
  employeeName: string;
  approverName: string | null;
  /** The working days a new request skips, in date order. Optional: frontend and backend deploy separately. */
  skippedDates?: string[] | null;
};

// ---- Home W4 Location widget --------------------------------------------------

export type LocationCount = {
  locationId: number;
  name: string;
  countryCode: string | null;
  count: number;
};

// ---- Home W5 Employment Status widget -----------------------------------------

export type IdCount = { id: number; count: number };

// ---- Home W6 Onboarding summary widget ----------------------------------------

export type OverduePerson = {
  employeeId: number;
  displayName: string;
  overdueTaskCount: number;
  daysOverdue: number;
};

export type UnfinishedPerson = {
  employeeId: number;
  displayName: string;
  unfinishedTaskCount: number;
};

export type OnboardingSummaryDto = {
  overdueCount: number;
  unfinishedCount: number;
  overduePeople: OverduePerson[];
  unfinishedPeople: UnfinishedPerson[];
};

// ---- Document module --------------------------------------------------------

export type DocumentKind = 'REGULAR' | 'SIGNING_TEMPLATE';

export type DocumentViewCategory =
  | 'REGULAR'
  | 'TEMPLATE_DRAFT'
  | 'PENDING_SIGNATURE'
  | 'SIGNED'
  | 'CANCELLED';

export type DocumentDto = {
  id: number;
  title: string;
  kind: DocumentKind;
  viewCategory: DocumentViewCategory;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  tags: string[];
  folderId: number | null;
  ownerEmployeeId: number | null;
  category: string | null;
  /** Original BambooHR upload date of a migrated file; null for in-app documents. */
  originalUploadedOn: string | null;
  signingDeadlineAt: string | null;
  createdAt: string;
  createdByUserId: number;
  updatedAt: string;
  /** USER for every uploaded document; LEAVE_REQUEST for a system-managed leave request form. */
  origin: DocumentOrigin;
};

export type DocumentOrigin = 'USER' | 'LEAVE_REQUEST';

export type DocumentSummaryDto = {
  id: number;
  title: string;
  kind: DocumentKind;
  viewCategory: DocumentViewCategory;
  sizeBytes: number;
  folderId: number | null;
  ownerEmployeeId: number | null;
  category: string | null;
  /** Original BambooHR upload date of a migrated file; null for in-app documents. */
  originalUploadedOn: string | null;
  signingDeadlineAt: string | null;
  createdAt: string;
  createdByUserId: number;
  /** When the requesting user signed it; only set on the signed-by listing, else null. */
  signedAt: string | null;
  /** The requesting user's SIGNED recipient row id; only set on the signed-by listing, else null. */
  recipientId: number | null;
};

export type EmergencyContactDto = {
  id: number;
  employeeId: number;
  name: string;
  relationship: string | null;
  phone: string | null;
  email: string | null;
  addressStreet1: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  countryOptionId: number | null;
  isPrimary: boolean;
};

/** Create/update payload for an employee's single emergency contact. */
export type SaveEmergencyContactCommand = {
  name: string;
  relationship: string | null;
  phone: string;
  email: string | null;
  addressStreet1: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  countryOptionId: number | null;
};

export type FolderDto = {
  id: number;
  name: string;
  documentCount: number;
  createdByUserId: number;
  createdAt: string;
};

// Kept as two types (not aliased) to mirror the Java api/SharingDto vs api/ShareCommand split:
// the response (SharingDto) is expected to diverge with per-row metadata, the command stays minimal.
export type SharingDto = {
  userIds: number[];
  allEmployees: boolean;
};

export type ShareCommand = {
  userIds: number[];
  allEmployees: boolean;
};

export type FieldType = 'SIGNATURE' | 'DATE' | 'NAME';

export type RecipientStatus = 'PENDING' | 'VIEWED' | 'SIGNED' | 'DECLINED';

export type RecipientDto = {
  id: number;
  documentId: number;
  userId: number;
  status: RecipientStatus;
  viewedAt: string | null;
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  createdAt: string;
};

export type FieldDto = {
  id: number;
  documentId: number;
  type: FieldType;
  pageIndex: number;
  // BigDecimal serialises to a JSON number from Spring's default ObjectMapper.
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
};

export type FieldPlacement = {
  type: FieldType;
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
};

export type SignedDocumentStatus = 'IN_PROGRESS' | 'CANCELLED';

export type SignedDocumentDto = {
  id: number;
  documentId: number;
  status: SignedDocumentStatus;
  createdAt: string;
};

export type SignatureAssetDto = {
  id: number;
  recipientId: number;
  createdAt: string;
};

export type FieldValueDto = {
  id: number;
  fieldId: number;
  value: string | null;
  signatureAssetId: number | null;
};

export type RecipientSigningView = {
  document: DocumentDto;
  recipient: RecipientDto;
  fields: FieldDto[];
  values: FieldValueDto[];
  signatureAsset: SignatureAssetDto | null;
};

export type SignDocumentRequest = {
  values: Record<number, string>;
  signatureAssetId: number | null;
};

// ---- My Info Job tab — history tables (Sections 2 & 3) -----------------------

export type EmploymentStatusHistoryDto = {
  id: number;
  employeeId: number;
  effectiveDate: string;
  employmentStatusId: number;
  comments: string | null;
  // Termination synthetic-row metadata (kept optional so existing rows/usages stay valid).
  // The synthetic Terminated row sets statusLabel="Terminated", systemRow=true, current=true,
  // employmentStatusId=null, and comments = the Type label.
  statusLabel?: string | null;
  systemRow?: boolean;
  current?: boolean;
};

// status is "ACTIVE" or "PENDING"; PENDING rows are Manager-submitted, awaiting approval, and only
// returned to an admin or the submitting manager (the profile owner never sees them).
export type JobInformationHistoryDto = {
  id: number;
  employeeId: number;
  effectiveDate: string;
  locationId: number | null;
  departmentId: number | null;
  jobTitleId: number | null;
  reportsToId: number | null;
  status: 'ACTIVE' | 'PENDING';
  submittedById: number | null;
  submittedAt: string | null;
};

export type EmploymentStatusEntryCommand = {
  effectiveDate: string;
  employmentStatusId: number;
  comment?: string | null;
};

export type JobInformationEntryCommand = {
  effectiveDate: string;
  locationId: number;
  departmentId: number;
  jobTitleId: number;
  reportsToId?: number | null;
  topOfOrg?: boolean;
};

// GET /v1/employees/{id}/pending-job-change — drives the Seniority "Pending: [value]" indicator.
export type PendingJobChangeDto = {
  pending: boolean;
  seniorityLevelOptionId: number | null;
};

// ---- Inbox (mirrors platform.inbox.NotificationDto) --------------------------

// One held field change on a field-change card (mirrors FieldChangePendingQuery.FieldChangeValues).
// Values are resolved server-side at read time from the encrypted approval payload; dropdown
// option ids arrive already resolved to their labels.
export type FieldChangeValueDto = {
  fieldKey: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
};

export type NotificationDto = {
  id: number;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  // Optional second link the detail drawer shows under the body with this label (the signed-document
  // notice's "View signed copy"); both null when the entry has none.
  detailLink: string | null;
  detailLinkLabel: string | null;
  folder: string;
  kind: 'ACTION' | 'INFORMATIONAL';
  status: string | null;
  statusLabel: string | null;
  partyRole: string;
  correlationType: string | null;
  correlationId: number | null;
  commentCount: number;
  hasUnseenComment: boolean;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Sub-folder grouping (mirrors the rail); null when the type maps to no sub-folder.
  category: InboxCategory | null;
  // Old/new values for field-change cards; null for every other type.
  changes: FieldChangeValueDto[] | null;
};

// Badge count: `count` is the number on the tray icon - the Inbox folder total. Always equals
// the Inbox total shown in the rail; Sent and Completed items are excluded.
export type InboxUnreadCountDto = {
  count: number;
};

// Sub-folder categories (mirrors platform.inbox.InboxCategory). A notification's category is
// derived server-side: INFORMATIONAL by kind, the rest by exact ACTION type.
export type InboxCategory =
  | 'TIME_OFF'
  | 'FIELD_CHANGE'
  | 'SIGNATURE'
  | 'PERFORMANCE'
  | 'ONBOARDING_OFFBOARDING'
  | 'INFORMATIONAL';

// One folder's counts: a total plus a per-category breakdown (the sub-folders). Sent is flat, so
// its categories map is empty; a category with zero items is omitted.
export type InboxFolderCount = {
  total: number;
  categories: Partial<Record<InboxCategory, number>>;
};

export type InboxFolderCounts = {
  inbox: InboxFolderCount;
  sent: InboxFolderCount;
  completed: InboxFolderCount;
};

export type InboxPage = {
  content: NotificationDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  last: boolean;
};

// Category selection lives in the folder rail (URL), not here; these are the in-view filters only.
export type InboxFiltersState = {
  dateRange: string;
  q: string;
};

export type InboxCommentDto = {
  id: number;
  authorId: number | null;
  authorName: string | null;
  body: string;
  isInternal: boolean;
  createdAt: string;
};

// ---- End Employment (termination) — PFHR-PROFILE-001i -------------------------

export type TerminationType = 'RESIGNATION' | 'TERMINATION';

// Closed attrition classification. Required on every termination; the wire contract with
// payforge-finance, so never rename a value. VOLUNTARY maps onto the legacy RESIGNATION type, the
// other three onto TERMINATION (the drawer derives the legacy pair, see terminationReason.ts).
export type TerminationReason = 'VOLUNTARY' | 'INVOLUNTARY' | 'CONTRACT_END' | 'OTHER';

// Resignation sub-type (only meaningful when type === 'RESIGNATION').
export type ResignationSubType = 'REGRETTABLE' | 'NON_REGRETTABLE';

export type RehireEligibility = 'YES' | 'NO' | 'NOT_SPECIFIED';

// Mirrors the backend FieldOptionView from /settings/employee-fields/TERMINATION_REASON/options.
// TERMINATION_REASON is a dropdown field (options in organization.field_option), NOT a bucket, so it
// is served by the /options endpoint and carries `label` (not `name`).
export type TerminationReasonOption = {
  id: number;
  label: string;
  archived: boolean;
};

// One direct-report reassignment instruction submitted with a termination.
export type ReassignmentCommand = {
  reportEmployeeId: number;
  newSupervisorId: number | null;
  topOfOrg: boolean;
};

// POST /v1/employees/{id}/terminate and PUT /v1/employees/{id}/pending-termination body.
export type TerminationRequest = {
  effectiveDate: string;
  terminationReason: TerminationReason;
  type: TerminationType;
  subType: ResignationSubType | null;
  reasonOptionId: number | null;
  rehire: RehireEligibility;
  regretted: boolean | null;
  notes: string | null;
  reassignments: ReassignmentCommand[];
};

// GET / PUT pending-termination payload. terminationReason is null only on a record scheduled
// before the reason existed; regretted and notes are null for the non-admin manager view.
export type PendingTerminationDto = {
  id: number;
  employeeId: number;
  terminationDate: string;
  terminationReason: TerminationReason | null;
  type: TerminationType;
  subType: ResignationSubType | null;
  reasonOptionId: number | null;
  rehire: RehireEligibility;
  regretted: boolean | null;
  notes: string | null;
  reassignments: ReassignmentCommand[];
  createdAt: string;
};

// PUT /v1/employees/{id}/termination-details body: sets or corrects the classification on an
// already-committed termination (the legacy-row data gap, or a later regretted / note correction).
export type UpdateTerminationDetailsRequest = {
  terminationReason: TerminationReason;
  regretted: boolean | null;
  terminationNote: string | null;
};

// Response of POST /terminate and PUT /pending-termination.
export type TerminationResponse = {
  employee: EmployeeDto;
  scheduled: boolean;
  pendingTermination: PendingTerminationDto | null;
};

// GET /v1/employees/{id}/termination/leave-conflicts?date=YYYY-MM-DD
export type LeaveConflictDto = {
  requestId: number;
  categoryId: number;
  categoryName: string;
  startDate: string;
  endDate: string;
  days: number;
};

// POST /v1/employees/{id}/pending-termination/cancel body.
export type CancelTerminationRequest = {
  notes: string | null;
};

// ---- Timesheet (semi-monthly periods, per-project manager approval) --------------

// How logged hours are classified. REGULAR is ordinary working time and the only type judged
// against the working-day target; OVERTIME is planned extra time; ON_CALL is a call-out worked
// while on call. Overtime and on-call rows can be started on a project only when its manager or
// an admin switched the type on for that project.
export type TimesheetTimeType = 'REGULAR' | 'OVERTIME' | 'ON_CALL';

export const TIME_TYPES: readonly TimesheetTimeType[] = ['REGULAR', 'OVERTIME', 'ON_CALL'];

export const TIME_TYPE_LABEL: Record<TimesheetTimeType, string> = {
  REGULAR: 'Regular',
  OVERTIME: 'Overtime',
  ON_CALL: 'On-call',
};

// One day's hours on one project inside a period view. `system` entries are the computed
// holiday / time-off overlay (read-only, never persisted; they report REGULAR).
export type TimesheetEntry = {
  projectId: number;
  projectName: string | null;
  category: string | null;
  hours: number;
  system: boolean;
  timeOffRequestId: number | null;
  timeType: TimesheetTimeType;
};

// `employed` is false on a day outside the employee's employment window (before the hire date or
// after the termination date): nothing can be booked on it, and the target leaves it out.
export type TimesheetDay = {
  date: string;
  weekend: boolean;
  employed: boolean;
  holidayName: string | null;
  entries: TimesheetEntry[];
};

// Approval state of one (employee, period, project) column. A column with no persisted
// slice row reports OPEN.
export type TimesheetSlice = {
  projectId: number;
  projectCode: string | null;
  projectName: string | null;
  clientName: string | null;
  managerName: string | null;
  status: 'OPEN' | 'SUBMITTED' | 'APPROVED';
  // Total across time types; the three below split it. One slice covers every type logged on the
  // project in the period, so the manager approves them together.
  hours: number;
  regularHours: number;
  overtimeHours: number;
  onCallHours: number;
  // The project's switches, so the grid knows whether it may offer an overtime / on-call row.
  overtimeAllowed: boolean;
  onCallAllowed: boolean;
  submittedAt: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  note: string | null;
};

// GET /v1/timesheet/period response. `exempt` is true when every weekday of the period is
// covered by a timesheet exemption: the employee is not required to submit, and any default
// allocations arrive as system entries.
export type TimesheetPeriodView = {
  employeeId: number;
  periodStart: string;
  periodEnd: string;
  derivedStatus: 'OPEN' | 'PARTIALLY_SUBMITTED' | 'SUBMITTED' | 'PARTIALLY_APPROVED' | 'APPROVED';
  targetHours: number;
  // Everything: logged hours of every type plus the overlay. The three below split the logged
  // part; only regular hours (plus the overlay) are meant to be read against targetHours.
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  onCallHours: number;
  holidaysUnavailable: boolean;
  exempt: boolean;
  // True when the employee may submit this period with no hours at all: they are not full-time
  // (they must file the timecard even without work), the period has started, and they were
  // employed on at least one of its days. That empty submission leaves no slice, so the period
  // reads SUBMITTED with nothing for an approver.
  emptySubmissionAllowed: boolean;
  // When the period was first submitted, with hours or without; null until it has been, and null
  // again once a withdrawal, send-back, reopen or cleared column leaves nothing submitted.
  periodSubmittedAt: string | null;
  slices: TimesheetSlice[];
  days: TimesheetDay[];
};

// Hours logged on one project across a whole period (approval-row breakdown).
export type TimesheetProjectHours = {
  projectId: number;
  projectName: string | null;
  hours: number;
};

// GET /v1/timesheet/pending-approvals and /recently-decided rows.
export type TimesheetApprovalSlice = {
  employeeId: number;
  employeeName: string;
  periodStart: string;
  periodEnd: string;
  projectId: number;
  projectCode: string | null;
  projectName: string | null;
  clientName: string | null;
  status: string;
  // The reviewed project's total across time types, and how much of it is overtime / on-call.
  sliceHours: number;
  sliceOvertimeHours: number;
  sliceOnCallHours: number;
  // Overtime plus on-call across the whole period: periodTotalHours - periodExtraHours is the
  // figure to read against targetHours.
  periodExtraHours: number;
  // Whole-period breakdown: the reviewed project first, then the employee's other
  // projects; overlayHours is the auto time-off/holiday block on top.
  allocations: TimesheetProjectHours[];
  overlayHours: number;
  periodTotalHours: number;
  targetHours: number;
  submittedAt: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
};

// A client a project belongs to, mirroring a Payforge Finance client. `financeClientId` is the
// Finance mapping key (null until a sync links it). GET /v1/settings/timesheet/clients.
export type TimesheetClient = {
  id: number;
  financeClientId: number | null;
  code: string;
  name: string;
  internal: boolean;
  active: boolean;
};

export type TimesheetProject = {
  id: number;
  code: string;
  name: string;
  category: 'CLIENT' | 'INTERNAL' | 'PUBLIC_HOLIDAY' | 'TIME_OFF';
  clientId: number | null;
  clientName: string | null;
  managerEmployeeId: number | null;
  managerName: string | null;
  managerActive: boolean;
  system: boolean;
  archived: boolean;
  // HR-only visibility: a hidden project stays in the catalog but leaves the employee
  // "Add project" picker (the /v1/timesheet/projects list never includes it).
  hidden: boolean;
  // Whether OVERTIME / ON_CALL rows may be started on the project (off by default; switched by
  // the project's manager or an admin).
  overtimeAllowed: boolean;
  onCallAllowed: boolean;
  financeWorkItemId: number | null;
  updatedAt: string | null;
};

// PUT /v1/settings/timesheet/projects/{id} body. The manager and the hidden flag are the two
// locally-owned project fields; code, name, category and client come from Payforge Finance and
// are read-only here.
export type TimesheetProjectStaffingRequest = {
  managerEmployeeId: number;
};

// PUT /v1/settings/timesheet/projects/{id}/hidden body.
export type TimesheetProjectHiddenRequest = {
  hidden: boolean;
};

// PUT /v1/settings/timesheet/projects/{id}/time-types (settings) and
// PUT /v1/timesheet/projects/{id}/time-types (project manager or admin) body.
export type TimesheetProjectTimeTypesRequest = {
  overtimeAllowed: boolean;
  onCallAllowed: boolean;
};

// PUT /v1/settings/timesheet/projects/{id}/finance-link body: exactly one of the two identifies
// the Finance root work item to point this project at. The repair for a link broken by a
// Finance-side archive/unarchive or delete/restore cycle.
export type TimesheetProjectFinanceLinkRequest = {
  financeWorkItemId: number | null;
  financeCode: string | null;
};

// PUT /v1/settings/timesheet/projects/{id}/finance-link response. absorbedProjectCode names the
// row that previously held the link (now unlinked + archived), null when none did;
// absorbedHadHours flags logged hours on it that may need review.
export type TimesheetProjectRelinkResult = {
  projectId: number;
  projectCode: string;
  financeWorkItemId: number;
  financeWorkItemCode: string | null;
  absorbedProjectCode: string | null;
  absorbedHadHours: boolean;
};

// GET/PUT /v1/settings/timesheet/finance-connection. baseUrl is the admin-set value (null =
// unset); effectiveBaseUrl is what the import will call (admin value, else the deployment's
// FINANCE_BASE_URL fallback, else null = not configured).
export type TimesheetFinanceConnection = {
  baseUrl: string | null;
  effectiveBaseUrl: string | null;
  environmentFallback: boolean;
};

// POST /v1/settings/timesheet/finance-import response. Counts reflect actual changes only;
// warnings are human-readable rows the import could not map cleanly.
export type TimesheetFinanceImportSummary = {
  clientsCreated: number;
  clientsUpdated: number;
  projectsCreated: number;
  projectsUpdated: number;
  projectsArchived: number;
  projectsRestored: number;
  warnings: string[];
};

// One approval slice reference: the (employee, period, project) triple decided as a unit.
export type TimesheetSliceRef = {
  employeeId: number;
  periodStart: string;
  projectId: number;
};

// POST /v1/timesheet/slices/approve-batch response. Partial success is normal: authority and
// state are re-checked per slice, so a stale selection reports skips instead of failing.
export type TimesheetBulkApproveResult = {
  approved: number;
  skipped: { slice: TimesheetSliceRef; reason: string }[];
};

export type TimesheetBulkRejectResult = {
  rejected: number;
  skipped: { slice: TimesheetSliceRef; reason: string }[];
};

export type TimesheetGridDay = {
  date: string;
  weekend: boolean;
};

// One member's hours of one time type on a project. Every involved member has a REGULAR row (the
// status and decision attach there); OVERTIME / ON_CALL rows appear only where such hours exist.
// employedFrom / employedUntil are the member's hire and termination dates (inclusive, null =
// open): the grid's days are shared by every member, so each row carries its own bookable range.
export type TimesheetProjectMemberRow = {
  employeeId: number;
  employeeName: string;
  timeType: TimesheetTimeType;
  status: 'OPEN' | 'SUBMITTED' | 'APPROVED';
  note: string | null;
  submittedAt: string | null;
  totalHours: number;
  hoursByDay: Record<string, number>;
  employedFrom: string | null;
  employedUntil: string | null;
};

export type TimesheetProjectGrid = {
  projectId: number;
  projectCode: string | null;
  projectName: string | null;
  clientId: number | null;
  clientName: string | null;
  managerName: string | null;
  archived: boolean;
  overtimeAllowed: boolean;
  onCallAllowed: boolean;
  rows: TimesheetProjectMemberRow[];
};

export type TimesheetProjectTimesheetsView = {
  periodStart: string;
  periodEnd: string;
  days: TimesheetGridDay[];
  projects: TimesheetProjectGrid[];
};

// GET /v1/settings/timesheet/data-quality. Staffing and config warnings are re-derived on every
// read so they cannot go stale; IMPORT warnings come from the stored last run, because they record
// what Finance sent and cannot be recomputed without calling Finance again.
export type TimesheetDataQualityWarning = {
  kind:
    | 'PROJECT_WITHOUT_MANAGER'
    | 'MANAGER_TERMINATED'
    | 'MANAGER_INACTIVE'
    | 'CLIENT_PROJECT_WITHOUT_CLIENT'
    | 'EXEMPTION_ON_ARCHIVED_PROJECT'
    | 'IMPORT';
  message: string;
  projectId: number | null;
};

export type TimesheetDataQualityReport = {
  /** When the catalog was last actually refreshed - a failed run does not move this. */
  lastImportAt: string | null;
  /** The most recent attempt failed, so the warnings below describe an older state. */
  lastImportFailed: boolean;
  lastImportError: string | null;
  warnings: TimesheetDataQualityWarning[];
};

// GET /v1/timesheet/approver-context response.
export type TimesheetApproverContext = {
  canApprove: boolean;
};

// Status of one semi-monthly half on the Month status dashboard. The five shared values are the
// period view's own derivedStatus; NOT_REQUIRED (the half asked nothing of this person - exempt,
// on leave, or outside their employment), NOT_DUE (the period is still running and nothing is
// logged - not late, just unfinished) and NOT_STARTED (a period that has ENDED with nothing
// logged) are the three the per-employee view has no word for.
export type TimesheetPeriodStatusCode =
  | 'NOT_REQUIRED'
  | 'NOT_DUE'
  | 'NOT_STARTED'
  | 'OPEN'
  | 'PARTIALLY_SUBMITTED'
  | 'PARTIALLY_APPROVED'
  | 'SUBMITTED'
  | 'APPROVED';

// One half-month of one employee's row on the Month status dashboard.
export type TimesheetPeriodStatus = {
  periodStart: string;
  periodEnd: string;
  status: TimesheetPeriodStatusCode;
  /** Manually logged hours only, every time type included. */
  loggedHours: number;
  /** The REGULAR part of loggedHours - the only part judged against expectedHours. */
  regularHours: number;
  overtimeHours: number;
  onCallHours: number;
  /** Logged plus the holiday / leave / exemption overlay - the employee's own period total. */
  coveredHours: number;
  /** Weekdays x 8h, the calendar target the employee's own page shows. */
  targetHours: number;
  /**
   * What this person has to log themselves: 8h per weekday they must account for, less any part of
   * a day a holiday or partial-day leave booking already fills.
   */
  expectedHours: number;
  /** Weekdays this person has to account for, whole or in part. */
  requiredDays: number;
  /** The period has ended, so its hours can be judged complete or missing. */
  due: boolean;
  exempt: boolean;
  /**
   * regularHours is short of expectedHours; false when the half required nothing or is not due.
   * Informational only: the short flag and the reminders go by missingDays.
   */
  underTarget: boolean;
  /**
   * The employee's current employment status is Full-time. Only full-time staff are judged on
   * hours; for anyone else hours completeness does not apply, only submitting does.
   */
  fullTime: boolean;
  /**
   * Required workdays whose regular hours fall short of what the day asks (8h, or what is left of
   * it after a partial-day leave booking; a holiday always takes the whole day). 0 until the half
   * is due; always 0 when not fullTime.
   */
  missingDays: number;
  /** The employee has no location, so no holiday overlay could be computed. */
  holidaysUnavailable: boolean;
  submittedAt: string | null;
  /**
   * When the half was first submitted as a whole, with hours or without; null until it has been.
   * For an employee who is not full-time this alone makes a half with no projects SUBMITTED.
   */
  periodSubmittedAt: string | null;
  openProjects: number;
  submittedProjects: number;
  approvedProjects: number;
};

// One employee's row on the Month status dashboard: both halves plus the rollup.
export type TimesheetEmployeeMonthStatus = {
  employeeId: number;
  employeeName: string;
  departmentId: number | null;
  departmentName: string | null;
  // ISO 3166 alpha-2 of the employee's location; null when they have no location set.
  countryCode: string | null;
  accountStatus: string | null;
  monthStatus: TimesheetPeriodStatusCode;
  monthLoggedHours: number;
  monthCoveredHours: number;
  monthOvertimeHours: number;
  monthOnCallHours: number;
  monthTargetHours: number;
  underTarget: boolean;
  // Full-time by current employment status; both halves carry the same value.
  fullTime: boolean;
  // Both halves' missingDays added up; always 0 when not full-time.
  missingDays: number;
  firstHalf: TimesheetPeriodStatus;
  secondHalf: TimesheetPeriodStatus;
};

// Every person counted once, bucketed by rollup status (whose turn it is).
export type TimesheetMonthTotals = {
  people: number;
  notStarted: number;
  inProgress: number;
  awaitingApproval: number;
  complete: number;
  notRequired: number;
  notDueYet: number;
  underTarget: number;
  // People with a workday missing hours in an ended half (full-time staff only); the cross-cut
  // the Missing hours filter counts.
  withMissingDays: number;
  // People with any overtime or on-call hours in the month; a cross-cut like underTarget.
  withExtraHours: number;
};

// GET /v1/timesheet/month-status?month=YYYY-MM response.
export type TimesheetMonthStatus = {
  month: string;
  firstHalfStart: string;
  secondHalfStart: string;
  monthEnd: string;
  totals: TimesheetMonthTotals;
  people: TimesheetEmployeeMonthStatus[];
};

// One default allocation of an exemption: hoursPerDay on the project per covered weekday.
export type TimesheetExemptionAllocation = {
  projectId: number;
  projectCode: string | null;
  projectName: string | null;
  clientName: string | null;
  hoursPerDay: number;
};

// GET /v1/settings/timesheet/exemptions row. endDate null = open-ended.
export type TimesheetExemption = {
  id: number;
  employeeId: number;
  employeeName: string;
  startDate: string;
  endDate: string | null;
  note: string | null;
  allocations: TimesheetExemptionAllocation[];
  // Whose name the generated approvals carry: last editor, falling back to the creator.
  authorizedByName: string | null;
  updatedAt: string | null;
};

// POST/PUT /v1/settings/timesheet/exemptions body.
export type TimesheetExemptionRequest = {
  employeeId: number;
  startDate: string;
  endDate: string | null;
  note: string | null;
  allocations: { projectId: number; hoursPerDay: number }[];
};

// ---- Timesheet reminders ------------------------------------------------------

// One pre-deadline reminder rule: fires N days before BOTH semi-monthly deadlines each month
// (0 = on the deadline day). nextOccurrence is the concrete date it fires next.
export type ReminderRuleDto = {
  id: number;
  daysBeforeDeadline: number;
  message: string | null;
  nextOccurrence: string | null;
};

// POST /v1/settings/timesheet/reminders/rules body. daysBeforeDeadline must be 0-12.
export type ReminderRuleRequest = {
  daysBeforeDeadline: number;
  message: string | null;
};

// The shared recurring overdue reminder: whether it runs, how many days apart the nags repeat
// (1-14) and how many days after a missed deadline they start (0-13; 0 = the 16th / the 1st).
export type ReminderSettingsDto = {
  overdueEnabled: boolean;
  overdueRepeatDays: number;
  overdueStartOffsetDays: number;
  version: number;
  updatedAt: string | null;
  updatedByName: string | null;
};

// PUT /v1/settings/timesheet/reminders/settings body; version is the optimistic-lock guard.
export type ReminderSettingsUpdateRequest = {
  overdueEnabled: boolean;
  overdueRepeatDays: number;
  overdueStartOffsetDays: number;
  version: number;
};

// GET /v1/settings/timesheet/reminders response: everything the admin Reminders tab shows.
export type ReminderConfigView = {
  rules: ReminderRuleDto[];
  settings: ReminderSettingsDto;
};

// One ended period the employee is still on the hook for; an approved period is never listed.
// allSubmitted = the employee's part is done and only the approver is pending (a softer note than
// work left to do): every project column submitted with every workday holding its hours.
export type OverduePeriodDto = {
  periodStart: string;
  periodEnd: string;
  label: string;
  allSubmitted: boolean;
};

// The running period once a pre-deadline reminder has fired for it while the employee still
// owes hours; deadlineDate is the period's end date.
export type PreDeadlineStatusDto = {
  periodStart: string;
  deadlineDate: string;
  label: string;
};

// The period containing today; owes = the employee still has hours to submit for it.
export type CurrentPeriodDto = {
  periodStart: string;
  deadlineDate: string;
  owes: boolean;
};

// The signed-in employee's own reminder standing, driving the in-app nudges (home banners and
// the time-off booking notice). Reminders are for full-time employees only, so anyone else, like
// a user with no employee record, gets an empty list and two nulls.
export type MyReminderStatusDto = {
  overduePeriods: OverduePeriodDto[];
  preDeadline: PreDeadlineStatusDto | null;
  currentPeriod: CurrentPeriodDto | null;
};

// ---- Google Sheets operational reporting -----------------------------------

export type GoogleSheetsSyncRun = {
  id: number;
  triggerType: 'MANUAL' | 'SCHEDULED';
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  timeOffFrom: string;
  timeOffTo: string;
  timesheetFrom: string;
  timesheetTo: string;
  timeOffRequests: number;
  timeOffBalances: number;
  timesheetActuals: number;
  timesheetStatuses: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
};

export type GoogleSheetsReportConfig = {
  key: string;
  tabName: string;
  description: string;
  enabled: boolean;
  columns: string[];
  availableColumns: string[];
};

export type GoogleSheetsReporting = {
  spreadsheetId: string | null;
  spreadsheetUrl: string | null;
  enabled: boolean;
  historyMonths: number;
  futureMonths: number;
  reports: GoogleSheetsReportConfig[];
  version: number;
  updatedAt: string | null;
  lastRun: GoogleSheetsSyncRun | null;
};

export type GoogleSheetsReportingRequest = {
  spreadsheetUrl: string | null;
  enabled: boolean;
  historyMonths: number;
  futureMonths: number;
  reports: { key: string; enabled: boolean; columns: string[] }[];
  version: number;
};
